import { FieldValues, UseFormReturn } from 'react-hook-form';
import {
    CheckboxElement,
    FormContainer,
    SelectElement,
    TextareaAutosizeElement,
    TextFieldElement,
} from 'react-hook-form-mui';
import { ArrowForward } from '@mui/icons-material';
import { CardActions, CardContent, IconButton, Stack } from '@mui/material';

import {
    EditButtons,
    hideNumberFieldStyle,
    ImageField,
    MaskedTextFieldElement,
    TripleColumnLayout,
} from '@barboxx/admin-components';
import { Place } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';
import {
    ImageKitCustomMetadataKeys,
    ImageKitFolders,
    ImageKitNamedTransforms,
    ImageKitTypes,
    PLACE_CATEGORY_TYPES,
} from '@barboxx/types';
import {
    enumToFormOptions,
    StateList,
    stateListToFormOptions,
} from '@barboxx/utils';

import { REACT_APP_ENV } from '../../../../../environments/environment';
import { HoursOfOperationSection } from './HoursOfOperation';

interface Props<T extends FieldValues> {
    formContext: UseFormReturn<T>;
    onSubmit: (values: T) => void;
    editableOnLoad?: boolean;
}

export function PlaceForm<T extends FieldValues = Place>({
    formContext,
    onSubmit,
    editableOnLoad = false,
}: Props<T>) {
    // Generated by Copilot
    const [editable, editableActions] = useBool(editableOnLoad);
    const { getValues } = formContext;
    const values = getValues() as unknown as Place;

    const visitWebsite = () => {
        if (values.website) {
            window.open(values.website, 'blank');
        }
    };

    const handleCancel = () => {
        formContext.reset(undefined);

        if (!editableOnLoad) {
            editableActions.off();
        }
    };

    return (
        <FormContainer onSuccess={onSubmit} formContext={formContext}>
            <CardContent>
                <Stack gap={2}>
                    <TripleColumnLayout
                        LeftContent={
                            <Stack gap={2}>
                                <TextFieldElement
                                    name="name"
                                    label="Name"
                                    slotProps={{
                                        input: { readOnly: !editable },
                                    }}
                                />
                                <TextFieldElement
                                    label="Street Address"
                                    name="streetAddress"
                                    slotProps={{
                                        input: { readOnly: !editable },
                                    }}
                                />
                                <TextFieldElement
                                    label="City"
                                    name="city"
                                    slotProps={{
                                        input: { readOnly: !editable },
                                    }}
                                />
                                <Stack direction="row" gap={1}>
                                    <SelectElement
                                        label="State"
                                        name="state"
                                        sx={{ flex: 1 }}
                                        options={stateListToFormOptions(
                                            StateList
                                        )}
                                        slotProps={{
                                            select: {
                                                readOnly: !editable,
                                            },
                                        }}
                                    />
                                    <TextFieldElement
                                        label="Zip Code"
                                        name="zip"
                                        sx={{ width: '7rem' }}
                                        slotProps={{
                                            input: {
                                                readOnly: !editable,
                                                sx: hideNumberFieldStyle,
                                            },
                                        }}
                                        type="number"
                                    />
                                </Stack>
                            </Stack>
                        }
                        CenterContent={
                            <Stack alignItems="center" gap={2} flex={1}>
                                <ImageField
                                    label="Main Image"
                                    name="mainImage"
                                    readOnly={!editable}
                                    imagekitUploadProps={{
                                        fileName: `${values.id}`,
                                        folder: ImageKitFolders.Places,
                                        env: REACT_APP_ENV,
                                        customMetadata: {
                                            [ImageKitCustomMetadataKeys.Id]:
                                                values['id'],
                                            [ImageKitCustomMetadataKeys.Type]:
                                                ImageKitTypes.Place,
                                            [ImageKitCustomMetadataKeys.Env]:
                                                REACT_APP_ENV,
                                        },
                                        tags: [
                                            values['name'],
                                            ImageKitTypes.Place,
                                            values['id'],
                                            REACT_APP_ENV,
                                        ],
                                    }}
                                    imagekitProps={{
                                        transformation: [
                                            {
                                                raw: ImageKitNamedTransforms.Web
                                                    .cover,
                                            },
                                        ],
                                        loading: 'lazy',
                                        lqip: {
                                            raw: ImageKitNamedTransforms.Web
                                                .coverLq,
                                        },
                                    }}
                                />
                            </Stack>
                        }
                        RightContent={
                            <Stack gap={2}>
                                <MaskedTextFieldElement
                                    name="phoneNumber"
                                    slotProps={{
                                        input: { readOnly: !editable },
                                    }}
                                    label="Phone Number"
                                    type="tel"
                                    inputProps={{
                                        mask: '+1 (#00) 000-0000',
                                        definitions: {
                                            '#': /[1-9]/,
                                        },
                                    }}
                                />
                                <Stack
                                    direction="row"
                                    gap={1}
                                    alignItems="center"
                                >
                                    <TextFieldElement
                                        label="Website"
                                        name="website"
                                        sx={{ flex: 1 }}
                                        slotProps={{
                                            input: { readOnly: !editable },
                                        }}
                                    />
                                    <IconButton
                                        sx={{ mt: 1 }}
                                        onClick={visitWebsite}
                                        disabled={
                                            !values['website'] ||
                                            !formContext.formState.isValid
                                        }
                                    >
                                        <ArrowForward />
                                    </IconButton>
                                </Stack>
                                <SelectElement
                                    name="category"
                                    label="Category"
                                    slotProps={{
                                        input: { readOnly: !editable },
                                    }}
                                    options={enumToFormOptions(
                                        PLACE_CATEGORY_TYPES
                                    )}
                                    fullWidth
                                />
                                <Stack direction="row" gap={1}>
                                    <TextFieldElement
                                        label="Latitude"
                                        name="latitude"
                                        type="number"
                                        sx={{ flex: 1 }}
                                        slotProps={{
                                            input: {
                                                readOnly: !editable,
                                                sx: hideNumberFieldStyle,
                                            },
                                        }}
                                    />
                                    <TextFieldElement
                                        label="Longitude"
                                        name="longitude"
                                        type="number"
                                        sx={{ flex: 1 }}
                                        slotProps={{
                                            input: {
                                                readOnly: !editable,
                                                sx: hideNumberFieldStyle,
                                            },
                                        }}
                                    />
                                </Stack>
                                <CheckboxElement
                                    name="enabled"
                                    label="Enabled"
                                    disabled={!editable}
                                />
                            </Stack>
                        }
                    />

                    <TextareaAutosizeElement
                        label="description"
                        name="description"
                        disabled={!editable}
                        sx={{ flex: 1, width: '100%' }}
                    />
                </Stack>
            </CardContent>

            <HoursOfOperationSection editable={editable} />
            <Can I="update" a="Place">
                <CardActions sx={{ justifyContent: 'flex-end' }}>
                    <EditButtons
                        formContext={formContext}
                        handleCancel={handleCancel}
                        editable={editable}
                        editableActions={editableActions}
                    />
                </CardActions>
            </Can>
        </FormContainer>
    );
}
